/**
 * Advanced Filters UI
 * 
 * Next-generation filtering interface with:
 * - Animated filter chips
 * - Visual range sliders
 * - Interactive toggles
 * - Real-time preview
 * - Smooth transitions
 * - Magnetic hover effects
 */

'use client';

import { useCurrencyOptional } from '@/contexts/CurrencyContext';
import { CURRENCY_CONFIG, DEFAULT_CURRENCY } from '@/lib/config/app.config';
import type { Currency } from '@/lib/config/app.config';
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export interface FilterOption {
  id: string;
  label: string;
  count?: number;
}

export interface AdvancedFiltersProps {
  statusOptions: FilterOption[];
  typeOptions: FilterOption[];
  priceRange: { min: number; max: number };
  unitsRange: { min: number; max: number };
  selectedStatus: string;
  selectedTypes: string[];
  selectedPriceRange: [number, number];
  selectedUnitsRange: [number, number];
  onStatusChange: (status: string) => void;
  onTypesChange: (types: string[]) => void;
  onPriceRangeChange: (range: [number, number]) => void;
  onUnitsRangeChange: (range: [number, number]) => void;
  onReset: () => void;
  messages: {
    filters: string;
    status: string;
    projectType: string;
    priceRange: string;
    units: string;
    reset: string;
    apply: string;
    showingResults: string;
  };
  accentColor?: string;
  className?: string;
}

/**
 * Advanced Filters Component
 */
export function AdvancedFilters({
  statusOptions,
  typeOptions,
  selectedStatus,
  selectedTypes,
  selectedPriceRange,
  selectedUnitsRange,
  priceRange,
  unitsRange,
  onStatusChange,
  onTypesChange,
  onPriceRangeChange,
  onUnitsRangeChange: _onUnitsRangeChange,
  onReset,
  messages,
  accentColor = '#BBA339',
  className = '',
}: AdvancedFiltersProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const currencyContext = useCurrencyOptional();
  const activeCurrency = currencyContext?.selectedCurrency ?? DEFAULT_CURRENCY;
  const currencySymbol = CURRENCY_CONFIG[activeCurrency as Currency]?.symbol ?? activeCurrency;

  const toggleType = (typeId: string) => {
    if (selectedTypes.includes(typeId)) {
      onTypesChange(selectedTypes.filter((t) => t !== typeId));
    } else {
      onTypesChange([...selectedTypes, typeId]);
    }
  };

  const formatPrice = (value: number) => {
    if (value >= 1000000) {
      return `${currencySymbol}${(value / 1000000).toFixed(1)}M`;
    }
    return `${currencySymbol}${(value / 1000).toFixed(0)}K`;
  };

  const activeFiltersCount =
    (selectedStatus !== 'ALL' ? 1 : 0) +
    selectedTypes.length +
    (selectedPriceRange[0] !== priceRange.min || selectedPriceRange[1] !== priceRange.max ? 1 : 0) +
    (selectedUnitsRange[0] !== unitsRange.min || selectedUnitsRange[1] !== unitsRange.max ? 1 : 0);

  return (
    <div className={`bg-white ${className}`}>
      {/* Filter Header */}
      <div className="border-b border-gray-200 px-6 py-4 lg:px-16">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h3 className="text-lg font-bold text-gray-900">{messages.filters}</h3>
            {activeFiltersCount > 0 && (
              <motion.div
                className="flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold text-white"
                style={{ backgroundColor: accentColor }}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 500, damping: 25 }}
              >
                {activeFiltersCount}
              </motion.div>
            )}
          </div>

          <div className="flex items-center gap-3">
            {activeFiltersCount > 0 && (
              <motion.button
                onClick={onReset}
                className="text-sm font-medium text-gray-600 hover:text-gray-900"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {messages.reset}
              </motion.button>
            )}

            <motion.button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <span>{isExpanded ? 'Hide' : 'Show'} Filters</span>
              <motion.svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                animate={{ rotate: isExpanded ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </motion.svg>
            </motion.button>
          </div>
        </div>
      </div>

      {/* Animated Filter Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="space-y-8 px-6 py-8 lg:px-16">
              {/* Status Filter */}
              <div>
                <label className="mb-3 block text-sm font-semibold text-gray-900">
                  {messages.status}
                </label>
                <div className="flex flex-wrap gap-2">
                  {statusOptions.map((option) => (
                    <motion.button
                      key={option.id}
                      onClick={() => onStatusChange(option.id)}
                      className={`group relative overflow-hidden rounded-full px-4 py-2 text-sm font-medium transition-all ${
                        selectedStatus === option.id
                          ? 'text-white shadow-lg'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      style={
                        selectedStatus === option.id
                          ? { backgroundColor: accentColor }
                          : {}
                      }
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      layout
                    >
                      <span className="relative z-10 flex items-center gap-2">
                        {option.label}
                        {option.count !== undefined && (
                          <span
                            className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                              selectedStatus === option.id
                                ? 'bg-white/20'
                                : 'bg-gray-200'
                            }`}
                          >
                            {option.count}
                          </span>
                        )}
                      </span>

                      {/* Animated Background */}
                      {selectedStatus === option.id && (
                        <motion.div
                          className="absolute inset-0"
                          style={{ backgroundColor: accentColor }}
                          layoutId="statusBackground"
                          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                        />
                      )}
                    </motion.button>
                  ))}
                </div>
              </div>

              {/* Project Type Filter */}
              <div>
                <label className="mb-3 block text-sm font-semibold text-gray-900">
                  {messages.projectType}
                </label>
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  {typeOptions.map((option) => {
                    const isSelected = selectedTypes.includes(option.id);

                    return (
                      <motion.button
                        key={option.id}
                        onClick={() => toggleType(option.id)}
                        className={`group relative overflow-hidden rounded-xl border-2 p-4 text-left transition-all ${
                          isSelected
                            ? 'border-current shadow-lg'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        style={isSelected ? { borderColor: accentColor } : {}}
                        whileHover={{ y: -2 }}
                        whileTap={{ scale: 0.98 }}
                        layout
                      >
                        {/* Checkbox */}
                        <div className="mb-2 flex items-center justify-between">
                          <motion.div
                            className={`flex h-5 w-5 items-center justify-center rounded border-2 ${
                              isSelected
                                ? 'border-current'
                                : 'border-gray-300'
                            }`}
                            style={isSelected ? { borderColor: accentColor, backgroundColor: accentColor } : {}}
                            animate={{ scale: isSelected ? [1, 1.2, 1] : 1 }}
                            transition={{ duration: 0.3 }}
                          >
                            {isSelected && (
                              <motion.svg
                                className="h-3 w-3 text-white"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                initial={{ pathLength: 0 }}
                                animate={{ pathLength: 1 }}
                                transition={{ duration: 0.3 }}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={3}
                                  d="M5 13l4 4L19 7"
                                />
                              </motion.svg>
                            )}
                          </motion.div>

                          {option.count !== undefined && (
                            <span className="text-xs font-semibold text-gray-500">
                              {option.count}
                            </span>
                          )}
                        </div>

                        {/* Label */}
                        <div className="text-sm font-medium text-gray-900">
                          {option.label}
                        </div>

                        {/* Hover Effect */}
                        <motion.div
                          className="absolute inset-0 opacity-0 group-hover:opacity-5"
                          style={{ backgroundColor: accentColor }}
                        />
                      </motion.button>
                    );
                  })}
                </div>
              </div>

              {/* Price Range Slider */}
              <div>
                <label className="mb-3 block text-sm font-semibold text-gray-900">
                  {messages.priceRange}
                </label>
                <div className="space-y-4">
                  <div className="flex items-center justify-between text-sm">
                    <motion.span
                      className="font-bold"
                      style={{ color: accentColor }}
                      key={selectedPriceRange[0]}
                      initial={{ scale: 1.2 }}
                      animate={{ scale: 1 }}
                    >
                      {formatPrice(selectedPriceRange[0])}
                    </motion.span>
                    <span className="text-gray-500">to</span>
                    <motion.span
                      className="font-bold"
                      style={{ color: accentColor }}
                      key={selectedPriceRange[1]}
                      initial={{ scale: 1.2 }}
                      animate={{ scale: 1 }}
                    >
                      {formatPrice(selectedPriceRange[1])}
                    </motion.span>
                  </div>

                  {/* Custom Range Slider Visualization */}
                  <div className="relative h-2 rounded-full bg-gray-200">
                    <motion.div
                      className="absolute h-full rounded-full"
                      style={{
                        backgroundColor: accentColor,
                        left: `${((selectedPriceRange[0] - priceRange.min) / (priceRange.max - priceRange.min)) * 100}%`,
                        right: `${100 - ((selectedPriceRange[1] - priceRange.min) / (priceRange.max - priceRange.min)) * 100}%`,
                      }}
                      layout
                    />
                  </div>

                  {/* HTML Range Inputs (styled minimally) */}
                  <div className="relative">
                    <input
                      type="range"
                      min={priceRange.min}
                      max={priceRange.max}
                      value={selectedPriceRange[0]}
                      onChange={(e) => onPriceRangeChange([Number(e.target.value), selectedPriceRange[1]])}
                      className="pointer-events-none absolute w-full opacity-0"
                    />
                    <input
                      type="range"
                      min={priceRange.min}
                      max={priceRange.max}
                      value={selectedPriceRange[1]}
                      onChange={(e) => onPriceRangeChange([selectedPriceRange[0], Number(e.target.value)])}
                      className="pointer-events-none absolute w-full opacity-0"
                    />
                  </div>
                </div>
              </div>

              {/* Units Range */}
              <div>
                <label className="mb-3 block text-sm font-semibold text-gray-900">
                  {messages.units}
                </label>
                <div className="space-y-4">
                  <div className="flex items-center justify-between text-sm">
                    <motion.span
                      className="font-bold"
                      style={{ color: accentColor }}
                      key={selectedUnitsRange[0]}
                      initial={{ scale: 1.2 }}
                      animate={{ scale: 1 }}
                    >
                      {selectedUnitsRange[0]}
                    </motion.span>
                    <span className="text-gray-500">to</span>
                    <motion.span
                      className="font-bold"
                      style={{ color: accentColor }}
                      key={selectedUnitsRange[1]}
                      initial={{ scale: 1.2 }}
                      animate={{ scale: 1 }}
                    >
                      {selectedUnitsRange[1]}
                    </motion.span>
                  </div>

                  {/* Units Range Visualization */}
                  <div className="relative h-2 rounded-full bg-gray-200">
                    <motion.div
                      className="absolute h-full rounded-full"
                      style={{
                        backgroundColor: accentColor,
                        left: `${((selectedUnitsRange[0] - unitsRange.min) / (unitsRange.max - unitsRange.min)) * 100}%`,
                        right: `${100 - ((selectedUnitsRange[1] - unitsRange.min) / (unitsRange.max - unitsRange.min)) * 100}%`,
                      }}
                      layout
                    />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

